import { useState, useEffect, useCallback } from 'react';
import { fetchAuditions } from '../services/auditionService';

/**
 * @hook useAuditions
 * @description 공고 목록 조회 + 상태 관리 훅
 *
 * 4-state: loading / error / empty / success
 *
 * @param {Object} params
 * @param {string} params.search    - 검색어
 * @param {string} params.category  - 카테고리 필터
 * @param {string} params.location  - 지역 필터
 * @param {string} params.status    - 상태 필터
 * @param {string} params.sort      - 정렬 기준
 * @param {number} params.page      - 현재 페이지
 * @param {number} [params.limit]         - 페이지당 개수 (기본 9)
 * @param {number} [params.firstPageLimit]- 1페이지만 다르게 줄 개수 (광고 카드 자리, 기본 limit과 동일)
 *
 * @returns {{
 *   data: AuditionItem[],
 *   total: number,
 *   totalPages: number,
 *   isLoading: boolean,
 *   isError: boolean,
 *   isEmpty: boolean,
 *   retry: Function,
 * }}
 */
function useAuditions({ search, category, location, status, sort, page, limit, firstPageLimit }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTotal, setActiveTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await fetchAuditions({ search, category, location, status, sort, page, ...(limit && { limit }), ...(firstPageLimit && { firstPageLimit }) });
      setData(result.data);
      setTotal(result.total);
      setActiveTotal(result.activeTotal ?? result.total);
      setTotalPages(result.totalPages);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [search, category, location, status, sort, page, limit, firstPageLimit]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    data,
    total,
    activeTotal,
    totalPages,
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && data.length === 0,
    retry: load,
  };
}

export default useAuditions;
